import { useState, MouseEvent } from "react"
import { Button, Menu, MenuItem, ListSubheader } from "@mui/material"
import { Compare, ArrowDropDown, InfoOutlined } from "@mui/icons-material"
import { useIsEndVsStartDateComparisonEnabled, useIsDashboardOptimizeExperimentsFeatureEnabled, useIsDashboardSelectionSizingFeatureEnabled } from "resources/account"
import OPTooltip from "components/material/tooltip"
import { WeightingStrategyType } from "connected-components/dashboard/components/top-bar/compare"
import "./comparison-menu.scss"

export default function ComparisonMenu({
  onInstrumentClick,
  onDateComparisonClick,
  isOptimizedExperimentsDrawerOpen,
  setIsOptimizedExperimentsDrawerOpen,
  onSetWeightStrategy,
}: ComparisonMenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null)
  const isEndVsStartDateComparisonEnabled = useIsEndVsStartDateComparisonEnabled()
  const isOptimizeExperimentsEnabled = useIsDashboardOptimizeExperimentsFeatureEnabled()
  const isSelectionSizingEnabled = useIsDashboardSelectionSizingFeatureEnabled()

  function handleToggle(event: MouseEvent<HTMLButtonElement>) {
    if (isEndVsStartDateComparisonEnabled || isOptimizeExperimentsEnabled) {
      setAnchor(event.currentTarget)
      setIsOpen(prev => !prev)
    } else {
      onInstrumentClick()
    }
  }

  function handleClose() {
    setIsOpen(false)
  }

  return (
    <>
      <Button
        variant="text"
        onClick={handleToggle}
        className="search-comparison-wrapper"
        startIcon={<Compare />}
      >
        <div className="search-comparison-label-container">
          <span>Compare</span>
          {(isEndVsStartDateComparisonEnabled || isOptimizeExperimentsEnabled) && <ArrowDropDown />}
        </div>
      </Button>
      <Menu
        anchorEl={anchor}
        open={isOpen}
        onClose={handleClose}
        disablePortal
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <MenuItem onClick={() => {
          onInstrumentClick()
          handleClose()
        }}
        >
          Instrument
        </MenuItem>
        <MenuItem onClick={() => {
          onDateComparisonClick()
          handleClose()
        }}
        >
          End vs start date
        </MenuItem>
        {isOptimizeExperimentsEnabled && (
          <MenuItem onClick={() => {
            setIsOptimizedExperimentsDrawerOpen(!isOptimizedExperimentsDrawerOpen)
            handleClose()
          }}
          >
            Optimize Experiments
          </MenuItem>
        )}
        {isSelectionSizingEnabled && [
          <ListSubheader key="strategy-selection-header">Decision Analysis</ListSubheader>,
          <MenuItem
            key="equal-weight"
            className="dashboard-comparison-menu-strategy-item"
            onClick={() => {
              onSetWeightStrategy("equally-weighted")
              handleClose()
            }}
          >
            <div className="dashboard-comparison-menu-strategy-item-content">
              <span>Equal-Sized</span>
              <OPTooltip title="Compare to an equal-weighted portfolio to show alpha gained or lost from sizing decisions.">
                <InfoOutlined className="dashboard-comparison-menu-info-icon" />
              </OPTooltip>
            </div>
          </MenuItem>,
        ]}
      </Menu>
    </>
  )
}

interface ComparisonMenuProps {
  onInstrumentClick: () => void
  onDateComparisonClick: () => void
  isOptimizedExperimentsDrawerOpen: boolean
  setIsOptimizedExperimentsDrawerOpen: (isOptimizedExperimentsDrawerOpen: boolean) => void
  onSetWeightStrategy: (weightStrategy: WeightingStrategyType) => void
}
